import { useState, useMemo } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Alert,
} from "reactstrap";

import { apiSlice } from "app/apiSlice";
import { showToast } from "components/Toasts/helpers/showToast";
import { PaymentConfirmationGrid } from "./PaymentConfirmationGrid";
import { type GroupPaymentModalProps, type MilestonePayment } from "../types";
import { formatAccountingAmount } from "../utils/formatters";

export const GroupPaymentModal = ({
  isOpen,
  onClose,
  milestones,
  groupType,
  groupName,
}: GroupPaymentModalProps) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [payMilestones] = apiSlice.usePayMilestonesMutation();

  const { totalAmount, validMilestones } = useMemo(() => {
    const valid = milestones.filter((m: MilestonePayment) => m._id);
    const total = valid.reduce((sum, milestone) => {
      const amount = Number(milestone.holdBalance ?? milestone.rate ?? 0);
      return sum + (Number.isFinite(amount) ? amount : 0);
    }, 0);
    return { totalAmount: total, validMilestones: valid };
  }, [milestones]);

  const getGroupDescription = () => {
    return (
      <>
        the following milestones for payment cycle <strong>{groupName}</strong>
      </>
    );
  };

  const handleConfirmPayment = async () => {
    if (validMilestones.length === 0) return;

    setIsSubmitting(true);
    setError(null);

    try {
      const milestoneIds = validMilestones.map((m) => m._id);
      await payMilestones({ milestoneIds }).unwrap();

      // Success - show success toast and close modal
      showToast({
        type: "success",
        message: `Successfully initiated payment(s) for ${validMilestones.length} milestone(s) totaling ${formatAccountingAmount(totalAmount)} for payment cycle ${groupName}.`,
      });
      onClose();
    } catch (err: any) {
      setError(
        err?.data?.message ||
          err?.message ||
          "Failed to process payments. Please try again.",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} toggle={onClose} size="lg">
      <ModalHeader toggle={onClose}>Confirm Group Payment</ModalHeader>
      <ModalBody>
        {error && (
          <Alert color="danger" className="mb-3">
            <strong>Payment Failed:</strong> {error}
          </Alert>
        )}

        <div className="mb-4">
          <p className="mb-2">
            Are you sure you want to pay {getGroupDescription()}?
          </p>
          <h5>Payment Summary</h5>
          <ul className="list-unstyled mb-0">
            <li>
              <strong>Number of milestones:</strong> {validMilestones.length}
            </li>
            <li>
              <strong>Total payment amount:</strong>{" "}
              {formatAccountingAmount(totalAmount)}
            </li>
          </ul>
        </div>

        {validMilestones.length > 0 && (
          <div>
            <h6>Milestones to be paid:</h6>
            <PaymentConfirmationGrid
              milestones={validMilestones}
              height={300}
            />
          </div>
        )}

        {validMilestones.length === 0 && (
          <Alert color="warning">No valid milestones found for payment.</Alert>
        )}
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={onClose} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button
          color="primary"
          onClick={handleConfirmPayment}
          disabled={
            isSubmitting || validMilestones.length === 0 || totalAmount <= 0
          }
        >
          {isSubmitting
            ? "Processing..."
            : `Pay ${formatAccountingAmount(totalAmount)}`}
        </Button>
      </ModalFooter>
    </Modal>
  );
};
